import { ACTIONS, TRIGGERS } from "@/constants/flows";
import { Action } from "@/models/flows";
import { useFlowStore } from "@/store/flow";
import { useState } from "react";

interface ActionSelectorProps {
  category: string;
  type: "starter" | "regular";
  action: Action;
}

const ActionSelector = ({ category, type, action }: ActionSelectorProps) => {
  const { nodes, setNodes, setActiveNode, setIsChoosingNode, edges, setEdges } =
    useFlowStore();
  const [isHovered, setIsHovered] = useState(false);

  const addNode = (label: string) => {
    let position;

    if (type === "starter") {
      // Dynamically calculate the center of the canvas
      const canvas = document.querySelector(".react-flow"); // Assuming ReactFlow has this class
      const canvasRect = canvas?.getBoundingClientRect();
      position = {
        x: canvasRect ? canvasRect.width / 3 : 500,
        y: canvasRect ? canvasRect.height / 6 : 300,
      };
    } else if (nodes.length > 0) {
      // Find the last empty node
      const lastEmptyNodeIndex = nodes.findIndex(
        (node) => node.type === "empty"
      );
      if (lastEmptyNodeIndex !== -1) {
        // Replace the last empty node
        const updatedNodes = [...nodes];
        const lastEmptyNode = updatedNodes[lastEmptyNodeIndex];

        updatedNodes[lastEmptyNodeIndex] = {
          ...lastEmptyNode,
          data: { label, type: action.type, category },
          type: "regular",
        };

        // Update the edges to connect the replaced node
        const updatedEdges = edges.map((edge) =>
          edge.target === lastEmptyNode.id
            ? { ...edge, target: updatedNodes[lastEmptyNodeIndex].id }
            : edge
        );

        setNodes(updatedNodes);
        setEdges(updatedEdges);
        setActiveNode(updatedNodes[lastEmptyNodeIndex]); // Set the replaced node as active
        setIsChoosingNode(false); // Hide the action picker menu
        return; // Exit after replacing the last empty node
      }

      // Place regular actions below the last known node
      const lastNode = nodes[nodes.length - 1];
      position = { x: lastNode.position.x, y: lastNode.position.y + 100 };
    } else {
      // Fallback for regular actions if no nodes exist
      position = { x: 100, y: 100 };
    }

    const node = {
      id: (nodes.length + 1).toString(),
      position,
      data: { label, type: action.type, category },
      type,
    };

    // Create an edge connecting the last node to the new node
    const newEdge = {
      id: `e${nodes[nodes.length - 1]?.id}-${node.id}`,
      source: nodes[nodes.length - 1]?.id,
      target: node.id,
      type: "default",
    };

    setNodes([...nodes, node]);
    setEdges([...edges, newEdge]);
    setActiveNode(node); // Set the new node as active
    setIsChoosingNode(false); // Hide the action picker menu
  };

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => addNode(action.label)}
      className={`relative group-hover:bg-${action.colour}-400/20 border mb-2 p-1.5 dark:border-white/10 rounded-xl dark:bg-zinc-700/20 dark:hover:bg-zinc-800 flex cursor-pointer group hover:border-${action.colour}-600 transition duration-200 ease-in-out`}
    >
      {isHovered && (
        <div className="absolute left-[-200px] top-0 -mt-0.5 w-48 px-3 pt-1 pb-2.5 dark:bg-dark-secondary border dark:border-white/5 text-white rounded-md shadow-lg">
          <p className="text-xs mt-1">{action.description}</p>
        </div>
      )}
      <div className={`p-1 bg-${action.colour}-400 rounded-lg`}>
        <action.icon className="w-4 h-4 text-white" />
      </div>
      <div className="ml-2 flex justify-center">
        <div className="flex items-center">
          <div className="">
            <h1 className={`text-[0.8rem] text-white/70 leading-tight`}>
              {action.label}
            </h1>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ActionPicker = () => {
  const { nodes } = useFlowStore();

  return (
    <div className="">
      <div>
        <div className="">
          {nodes.length > 0 ? (
            Object.keys(ACTIONS).map((category, index) => (
              <div className="mb-4" key={index}>
                <h4
                  data-sidebar="group-label"
                  className="pb-2 border-white/5 text-xs text-stone-400 flex h-8 shrink-0 items-center text-sm font-medium text-sidebar-foreground/70 outline-none ring-sidebar-ring transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&amp;>svg]:size-4 [&amp;>svg]:shrink-0 group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0"
                >
                  {category}
                </h4>
                {ACTIONS[category].map((action: Action) => (
                  <ActionSelector
                    category={category}
                    type="regular"
                    action={action}
                  />
                ))}
              </div>
            ))
          ) : (
            <div>
              {TRIGGERS.map((action: Action) => (
                <ActionSelector
                  category="Trigger"
                  type="starter"
                  action={action}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
